/* eslint-disable react-hooks/rules-of-hooks */
import { CartDrawerSection } from "_types";
import { PromotionalItems } from "cart-drawer.promotional-items";
import { initRouteProtectionItems } from "cart-drawer.route-protection";
import { CartSideEffects } from "cart-drawer.side-effects";
import clsx from "clsx";

import { initGiftDiscounts } from "gift-with-purchase";
import {
  _Product,
  _Variant,
  cartDrawer,
  useCartDrawer,
  useGlobalProducts,
  useGlobalSettings,
} from "global-stores";
import { CloseIcon, TimerIcon } from "icons";
import { Image } from "image";
import { CartLineItems } from "main-cart";
import { render } from "preact";
import { ReactProductState } from "product";
import { ProductQuantitySelector } from "product.quantity_selector";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { _Cart_fetch_api, _Cart_fetch_api_Items } from "types";
import { capitalize, JSONParse } from "utils";
import { StoreApi, UseBoundStore, create } from "zustand";
import {
  CONFIGURABLE_PRODUCTS_LINE_ITEM_UUID_CHILD_KEY,
  CONFIGURABLE_PRODUCTS_LINE_ITEM_UUID_PARENT_KEY,
} from "./product-configurable-products.constants";

export type QuantitySelectorState = {
  quantity: number;
  setQuantity: (quantity: number) => void;
  formId: string;
  selectedVariant: _Variant;
  product: _Product;
};

// Component to handle dynamic horizontal line positioning
const HorizontalLine: FC = ({
  isChildItem,
  imageContainerRef,
  articleRef,
  isFirstChild,
  parentImageRef,
  parentContainerRef,
}) => {
  const [lineStyle, setLineStyle] = useState<{
    left: string;
    top: string;
    width: string;
  } | null>(null);

  const calculateLinePosition = useCallback(() => {
    if (
      !isChildItem ||
      !imageContainerRef?.current ||
      !articleRef?.current ||
      !parentImageRef?.current ||
      !parentContainerRef?.current
    ) {
      return;
    }

    const imageRect = imageContainerRef.current.getBoundingClientRect();
    const articleRect = articleRef.current.getBoundingClientRect();
    const parentImageRect = parentImageRef.current.getBoundingClientRect();
    const parentContainerRect = parentContainerRef.current.getBoundingClientRect();

    // Calculate center Y of image relative to article
    const imageCenterY = imageRect.top + imageRect.height / 2 - articleRect.top;

    // Position line at the center of the image
    const lineTop = `${imageCenterY}px`;

    // Calculate where the vertical line is (left edge of parent image + 10px offset)
    const verticalLineX = parentImageRect.left - parentContainerRect.left + 10;

    // Calculate where this article starts relative to parent container
    const articleStartX = articleRect.left - parentContainerRect.left;

    // Left position: from where the vertical line is (relative to article)
    const lineLeft = `${verticalLineX - articleStartX}px`;

    // Width: from vertical line to left edge of child image
    const imageLeftX = imageRect.left - articleRect.left;
    const lineWidth = `${imageLeftX - (verticalLineX - articleStartX)}px`;

    setLineStyle({
      left: lineLeft,
      top: lineTop,
      width: lineWidth,
    });
  }, [isChildItem, imageContainerRef, articleRef, parentImageRef, parentContainerRef]);

  useEffect(() => {
    if (isChildItem) {
      calculateLinePosition();

      // Recalculate on resize
      const handleResize = () => {
        setTimeout(calculateLinePosition, 0);
      };

      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, [isChildItem, calculateLinePosition]);

  // Use ResizeObserver for more accurate recalculation
  useEffect(() => {
    if (
      !isChildItem ||
      !imageContainerRef?.current ||
      !articleRef?.current ||
      !parentImageRef?.current ||
      !parentContainerRef?.current
    )
      return;

    const resizeObserver = new ResizeObserver(() => {
      setTimeout(calculateLinePosition, 0);
    });

    resizeObserver.observe(imageContainerRef.current);
    resizeObserver.observe(articleRef.current);
    resizeObserver.observe(parentImageRef.current);
    resizeObserver.observe(parentContainerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [
    isChildItem,
    imageContainerRef,
    articleRef,
    parentImageRef,
    parentContainerRef,
    calculateLinePosition,
  ]);

  if (!isChildItem || !lineStyle) return null;

  return <div className="absolute z-10 h-px min-w-px bg-[#d1d3d4]" style={lineStyle} />;
};

// Component to handle dynamic vertical line positioning
const VerticalConnector: FC = ({
  parentImageRef,
  lastChildImageRef,
  parentContainerRef,
  firstChildArticleRef,
}) => {
  const [lineStyle, setLineStyle] = useState<{
    left: string;
    top: string;
    bottom: string;
  } | null>(null);

  const calculateLinePosition = useCallback(() => {
    if (!parentImageRef.current || !lastChildImageRef.current || !parentContainerRef.current) {
      return;
    }

    const parentImageRect = parentImageRef.current.getBoundingClientRect();
    const lastChildImageRect = lastChildImageRef.current.getBoundingClientRect();
    const containerRect = parentContainerRef.current.getBoundingClientRect();

    // Calculate bottom Y of parent image (from left edge) relative to container
    const parentImageBottomY = parentImageRect.bottom - containerRect.top;

    // Calculate center Y of last child image relative to container
    const lastChildImageCenterY =
      lastChildImageRect.top + lastChildImageRect.height / 2 - containerRect.top;

    // Calculate left position: should start from the left edge of parent image + offset (moved slightly to the right)
    const lineLeft = `${parentImageRect.left - containerRect.left + 10}px`;

    // Top starts from bottom of parent image (where the red dot is - borde izquierdo abajo)
    const lineTop = `${parentImageBottomY}px`;

    // Bottom should end at last child image center Y
    const lineBottom = `${containerRect.height - lastChildImageCenterY}px`;

    setLineStyle({
      left: lineLeft,
      top: lineTop,
      bottom: lineBottom,
    });
  }, [parentImageRef, lastChildImageRef, parentContainerRef, firstChildArticleRef]);

  useEffect(() => {
    calculateLinePosition();

    // Recalculate on resize
    const handleResize = () => {
      setTimeout(calculateLinePosition, 0);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [calculateLinePosition]);

  // Use ResizeObserver for more accurate recalculation
  useEffect(() => {
    if (!parentImageRef.current || !lastChildImageRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      setTimeout(calculateLinePosition, 0);
    });

    resizeObserver.observe(parentImageRef.current);
    resizeObserver.observe(lastChildImageRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [parentImageRef, lastChildImageRef, calculateLinePosition]);

  if (!lineStyle) return null;

  return <div className="absolute w-px bg-[#d1d3d4]" style={lineStyle} />;
};

export const CartDrawerLineItems = () => {
  const { cartData } = useCartDrawer(({ cartData }) => ({ cartData }));

  // Get all items including children for proper nesting
  const allItems = cartData?.items?.filter((item) => item?.quantity > 0) || [];

  // Separate parent and child items
  const parentItems = allItems.filter(
    (item) => !item.properties[CONFIGURABLE_PRODUCTS_LINE_ITEM_UUID_CHILD_KEY]
  );
  const childItems = allItems.filter(
    (item) => item.properties[CONFIGURABLE_PRODUCTS_LINE_ITEM_UUID_CHILD_KEY]
  );

  return (
    <>
      {parentItems?.length && (
        <>
          {parentItems.map((line_item, index) => {
            const parentUuid =
              line_item.properties[CONFIGURABLE_PRODUCTS_LINE_ITEM_UUID_PARENT_KEY];
            const relatedChildren = parentUuid
              ? childItems.filter(
                  (child) =>
                    child.properties[CONFIGURABLE_PRODUCTS_LINE_ITEM_UUID_CHILD_KEY] === parentUuid
                )
              : [];

            const parentImageRef = useRef<HTMLDivElement>(null);
            const lastChildImageRef = useRef<HTMLDivElement>(null);
            const firstChildArticleRef = useRef<HTMLElement>(null);
            const parentContainerRef = useRef<HTMLDivElement>(null);

            const isLastParent = index === parentItems.length - 1;

            return (
              <div
                key={line_item?.key}
                ref={parentContainerRef}
                className={`relative px-4 ${
                  isLastParent ? "border-b-0" : "border-b border-gray-200"
                }`}
              >
                <CartDrawerLineItem
                  line_item={line_item}
                  index={index}
                  line_items={allItems}
                  isFirstParent={index === 0}
                  hasChildren={relatedChildren.length > 0}
                  imageRef={parentImageRef}
                />
                {/* Render child items nested under parent */}
                {relatedChildren.length > 0 && (
                  <>
                    {/* Vertical line from parent image to middle of last child image */}
                    <VerticalConnector
                      parentImageRef={parentImageRef}
                      lastChildImageRef={lastChildImageRef}
                      parentContainerRef={parentContainerRef}
                      firstChildArticleRef={firstChildArticleRef}
                    />
                    <div className="mb-0 pb-0 pl-[calc(1rem+24px)] pr-6 pt-0 font-[Poppins] text-sm font-semibold leading-none tracking-normal text-[#626d7e] sm:pr-6">
                      Add-ons:
                    </div>
                    <div className="mt-2 flex flex-col gap-0 pb-6">
                      {relatedChildren.map((childItem, childIndex) => (
                        <CartDrawerLineItem
                          key={childItem?.key}
                          line_item={childItem}
                          index={allItems.findIndex((item) => item.key === childItem.key)}
                          line_items={allItems}
                          isChildItem={true}
                          isLastChild={childIndex === relatedChildren.length - 1}
                          isFirstChild={childIndex === 0}
                          totalChildren={relatedChildren.length}
                          imageRef={
                            childIndex === relatedChildren.length - 1
                              ? lastChildImageRef
                              : undefined
                          }
                          articleRef={childIndex === 0 ? firstChildArticleRef : undefined}
                          parentImageRef={parentImageRef}
                          parentContainerRef={parentContainerRef}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </>
      )}
    </>
  );
};

export const CartLastAddedItems = () => {
  const { cartData } = useCartDrawer(({ cartData }) => ({ cartData }));
  const [lastCartItems, setLastCartItems] = useState<_Cart_fetch_api_Items[]>([]);
  const timeout = useRef<NodeJS.Timeout>(null);

  useEffect(() => {
    if (!lastCartItems?.length && window?.lastCartItems?.length) {
      setLastCartItems(window.lastCartItems);
    }
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
    timeout.current = setTimeout(() => {
      document.dispatchEvent(new CustomEvent("cart-added-modal:close"));
      setLastCartItems([]);
    }, 3000);
  }, [cartData.item_count, lastCartItems.length]);

  return (
    <div>
      {window.lastCartItems?.map((item) => (
        <div key={item.key} className="grid gap-4">
          <Image
            src={item?.featured_image?.url}
            width={50}
            height={50}
            alt={item?.featured_image?.alt ?? item.title}
          />
          <div className="product-card-title">{item.title}</div>
        </div>
      ))}
    </div>
  );
};

export const CartDrawerLineItem: FC = ({
  line_items,
  line_item,
  index,
  isChildItem = false,
  isFirstParent = false,
  isLastChild = false,
  hasChildren = false,
  isFirstChild = false,
  totalChildren = 0,
  imageRef,
  articleRef: externalArticleRef,
  parentImageRef,
  parentContainerRef,
}) => {
  const { adjustItem, routeProtectionProducts, cartData } = useCartDrawer(
    ({ adjustItem, routeProtectionProducts, cartData }) => ({
      adjustItem,
      routeProtectionProducts,
      cartData,
    })
  );

  const [lineItemProduct, setLineItemProduct] = useState<_Product>(null);
  const [lineItemVariant, setLineItemVariant] = useState<_Variant>(null);
  const { hydrated } = useGlobalProducts(({ hydrated }) => ({ hydrated }));

  const {
    cart_order_discounts,
    product_data__preorder_metafield,
    product_data__preorder_limit_metafield,
  } = useGlobalSettings(
    ({
      cart_order_discounts,
      product_data__preorder_metafield,
      product_data__preorder_limit_metafield,
    }) => ({
      cart_order_discounts,
      product_data__preorder_metafield,
      product_data__preorder_limit_metafield,
    })
  );

  const routeProtection = routeProtectionProducts.find(
    (block) => block?.products?.[0]?.handle === line_item?.handle
  );

  const isSingleQuantityRouteProtectionItem = !!routeProtection?.limit_quantity;

  const children_prices = cartData.items.reduce((acc, line) => {
    if (
      line.properties[CONFIGURABLE_PRODUCTS_LINE_ITEM_UUID_CHILD_KEY] ===
      line_item.properties[CONFIGURABLE_PRODUCTS_LINE_ITEM_UUID_PARENT_KEY]
    ) {
      return acc + line.final_line_price;
    }

    return acc;
  }, 0);

  const isConfigurableProduct =
    !!line_item.properties[CONFIGURABLE_PRODUCTS_LINE_ITEM_UUID_PARENT_KEY];

  const final_price =
    cart_order_discounts === "inline" ? line_item?.discounted_price : line_item?.final_price;

  const maxQuantity = useMemo(() => {
    if (isSingleQuantityRouteProtectionItem || !lineItemVariant) {
      return 1;
    }

    const { inventory_management, inventory_policy, inventory_quantity } = lineItemVariant;
    const continueSelling = inventory_policy === "continue" || inventory_management !== "shopify";

    if (!continueSelling) {
      return Number.MAX_SAFE_INTEGER;
    }

    const [preorder_ns, preorder_key] = product_data__preorder_metafield.split(".");
    const [limit_ns, limit_key] = product_data__preorder_limit_metafield.split(".");
    const pre_order =
      lineItemVariant?.metafields?.[preorder_ns]?.[preorder_key] ??
      lineItemProduct?.metafields?.[preorder_ns]?.[preorder_key];
    const pre_order_limit = +(lineItemVariant?.metafields?.[limit_ns]?.[limit_key] ?? 0);

    if (pre_order) {
      return Math.max(inventory_quantity + pre_order_limit, 1);
    }

    return Number.MAX_SAFE_INTEGER;
  }, [
    isSingleQuantityRouteProtectionItem,
    lineItemProduct?.metafields,
    lineItemVariant,
    product_data__preorder_limit_metafield,
    product_data__preorder_metafield,
  ]);

  const useQuantitySelectorStore = useMemo(() => {
    return create<QuantitySelectorState>(() => ({
      quantity: line_item?.quantity ?? 1,
      formId: `cart-drawer-${line_item?.key ?? index}`,
      selectedVariant: lineItemVariant,
      product: lineItemProduct,
      setQuantity: (nextQuantity) => {
        const boundedQuantity = Math.max(0, Math.min(nextQuantity, maxQuantity));
        const delta = boundedQuantity - line_item.quantity;
        if (delta !== 0) {
          adjustItem(line_item, delta, index);
        }
      },
    }));
  }, [adjustItem, index, line_item, lineItemProduct, lineItemVariant, maxQuantity]);

  useEffect(() => {
    useQuantitySelectorStore.setState({
      quantity: line_item?.quantity ?? 1,
      selectedVariant: lineItemVariant,
      product: lineItemProduct,
      formId: `cart-drawer-${line_item?.key ?? index}`,
    });
  }, [
    index,
    line_item?.key,
    line_item?.quantity,
    lineItemProduct,
    lineItemVariant,
    useQuantitySelectorStore,
  ]);

  useEffect(() => {
    if ((!lineItemVariant || !lineItemProduct) && hydrated) {
      window.useGlobalProducts
        .getState()
        .getAsyncProduct(line_item?.handle)
        .then((p) => {
          setLineItemVariant(p?.variants?.find(({ id }) => id === line_item?.variant_id));
          setLineItemProduct(p);
        });
    }
  }, [hydrated, line_item?.handle, line_item?.variant_id, lineItemProduct, lineItemVariant]);

  const internalArticleRef = useRef<HTMLElement>(null);
  const articleRef = externalArticleRef || internalArticleRef;
  const internalImageRef = useRef<HTMLDivElement>(null);
  const finalImageRef = imageRef || internalImageRef;

  return (
    <article
      ref={articleRef}
      className={clsx(
        "flex items-start space-x-5",
        isChildItem ? "relative pl-[calc(1rem)] pt-2" : "py-6"
      )}
    >
      <HorizontalLine
        isChildItem={isChildItem}
        imageContainerRef={finalImageRef}
        articleRef={articleRef}
        isFirstChild={isFirstChild}
        parentImageRef={parentImageRef}
        parentContainerRef={parentContainerRef}
      />
      <div className="relative block self-center">
        <div
          ref={finalImageRef}
          className={clsx(
            "relative overflow-hidden border-2 border-white shadow-[0_0_1.78px_0_rgba(0,0,0,0.33)]",
            isSingleQuantityRouteProtectionItem
              ? "h-10 w-24 rounded-[5px] p-[1px]"
              : isChildItem
                ? "h-14 w-14 rounded-[5px] p-[1px]"
                : "h-24 w-24 rounded-[5px] p-[1px]"
          )}
        >
          <Image
            src={`${line_item?.image}&crop=center&height=200&width=200`}
            width={200}
            height={200}
            className={clsx(
              "h-full w-full rounded-[5px] object-cover",
              !line_item?.image && "pointer-events-none hidden overflow-hidden opacity-0"
            )}
            alt={line_item?.featured_image?.alt ?? line_item?.title}
          />
        </div>
        {!isSingleQuantityRouteProtectionItem && (
          <div className="absolute -right-2 -top-2 z-10 flex h-6 w-6 items-center justify-center rounded-lg border-2 border-[#004395] bg-[#004395] text-xs font-semibold text-white">
            {line_item?.quantity}
          </div>
        )}
      </div>
      <main className="flex flex-1 items-start justify-between gap-4">
        <div className="flex flex-1 flex-col">
          <div className="flex items-baseline justify-between gap-2 pt-4">
            <div className="flex-1">
              {isChildItem ? (
                <span className="font-[Poppins] text-[14px] font-medium leading-none tracking-normal text-[rgba(1,50,111,1)]">
                  {line_item?.product_title}
                </span>
              ) : (
                <a
                  className="font-[Poppins] text-[14px] font-medium leading-none tracking-normal text-[rgba(1,50,111,1)] hf:underline"
                  href={line_item?.url}
                >
                  {line_item?.product_title}
                </a>
              )}
            </div>
            {!isChildItem && (
              <div className="shrink-0 whitespace-nowrap text-sm font-medium">
                {isConfigurableProduct ? (
                  <span
                    className="text-black"
                    dangerouslySetInnerHTML={{ __html: window.formatMoney(final_price) }}
                  ></span>
                ) : (
                  <>
                    {!isSingleQuantityRouteProtectionItem && (
                      <span
                        className={clsx(
                          "text-black/50 line-through",
                          line_item?.original_price === final_price &&
                            "pointer-events-none hidden overflow-hidden opacity-0"
                        )}
                        dangerouslySetInnerHTML={{
                          __html: `${window.formatMoney(line_item?.original_price)} `,
                        }}
                      ></span>
                    )}
                    <span
                      className="text-black"
                      dangerouslySetInnerHTML={{ __html: window.formatMoney(final_price) }}
                    ></span>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Show only first description line for child items, or variant title for parent items */}
          {isChildItem ? (
            <h4 className="text-xs text-gray-500 ">{line_item?.variant_title}</h4>
          ) : (
            !isSingleQuantityRouteProtectionItem && (
              <h4 className="text-xs text-gray-500 ">{line_item?.variant_title}</h4>
            )
          )}

          {/* Show properties only for parent items and filter out extra options */}
          {!isChildItem &&
            Object.entries(line_item?.properties ?? {})
              ?.filter(([key]) => !/^_/gi.test(key))
              ?.map(([key, value]) => {
                if (key === "preorder") {
                  return (
                    <p key={key} className="mt-1 flex text-xs text-gray-600 opacity-70">
                      <TimerIcon className="mr-2 h-4 w-4" /> <span>{value}</span>
                    </p>
                  );
                }
                return (
                  <p key={key} className="text-xs text-gray-600">
                    {capitalize(key)}: {value}
                  </p>
                );
              })}

          {line_item?.selling_plan_allocation?.selling_plan?.name ? (
            <p className="text-xs text-gray-600">
              {line_item?.selling_plan_allocation?.selling_plan?.name}
            </p>
          ) : null}

          {isSingleQuantityRouteProtectionItem ? null : line_item?.discounts?.length &&
            final_price === line_item?.discounted_price ? (
            <div className="text-xs font-medium opacity-50">
              {line_item?.discounts?.map((discount) => (
                <div key={discount.title}>Discount: {discount.title}</div>
              ))}
            </div>
          ) : null}
          {!hasChildren && !isChildItem && !isSingleQuantityRouteProtectionItem && (
            <div className="mt-3 max-w-[140px]">
              <ProductQuantitySelector
                useProduct={useQuantitySelectorStore}
                showText={false}
                allowZero={true}
                size="sm"
              />
            </div>
          )}
        </div>

        {!isChildItem && (
          <button
            type="button"
            className="hf:text-theme-text/ absolute right-4 top-[16px] flex shrink-0 self-start rounded-theme text-theme-text/90 transition-all md:right-0"
            onClick={() => {
              adjustItem(line_item, -line_item?.quantity, index);
            }}
          >
            <span className="sr-only">Remove item from Cart</span>
            <CloseIcon className="h-5 w-5" />
          </button>
        )}
        {isChildItem && (
          <div className="flex flex-col items-end text-right">
            <div className="whitespace-nowrap text-sm font-medium">
              <span
                className="text-black"
                dangerouslySetInnerHTML={{ __html: window.formatMoney(final_price) }}
              ></span>
            </div>
          </div>
        )}
      </main>
    </article>
  );
};

export const initLineItems = async () => {
  const cartDrawerLineItems = document.querySelector<HTMLElement>("[data-cart-drawer-line-items]");
  const cartLineItems = document.querySelector<HTMLElement>("[data-cart-line-items]");
  const cartSideEffects = document.querySelector<HTMLElement>("[data-cart-side-effects]");

  await cartDrawer.getState().initCart();

  if (cartDrawerLineItems) {
    cartDrawerLineItems.innerHTML = "";
    render(null, cartDrawerLineItems);
    render(<CartDrawerLineItems />, cartDrawerLineItems);
  }
  if (cartLineItems) {
    cartLineItems.innerHTML = "";
    render(<CartLineItems />, cartLineItems);
  }
  if (cartSideEffects) {
    render(<CartSideEffects />, cartSideEffects);
  }
};

export const initNonDrawerModal = async () => {
  const drawerAddedItems = document.querySelector<HTMLElement>("[data-drawer-added-items]");

  if (drawerAddedItems) {
    render(<CartLastAddedItems />, drawerAddedItems);
  }
};

export const initCart = async () => {
  initAnnouncementBars();
  initFreeShippingBar();
  initLineItems();
  initNonDrawerModal();
  initPromotionalItems();
  initRouteProtectionItems();
  initGiftDiscounts();
};

function initAnnouncementBars() {
  const announcementBars = document.querySelectorAll<HTMLElement>(
    "[data-announcement-bar]:not([data-initiated])"
  );

  announcementBars.forEach((announcementBar) => {
    announcementBar.setAttribute("data-initiated", "");
    let activeIndex = 0;

    displayAnnouncements();

    function displayAnnouncements() {
      [...announcementBar.querySelectorAll<HTMLElement>("[data-announcement-index]")].forEach(
        (announcement, i, arr) => {
          const index = +announcement.dataset.announcementIndex;
          const duration = +announcement.dataset.announcementDuration;
          const textColor = announcement.dataset.announcementText;
          const bgColor = announcement.dataset.announcementBg;

          if (arr.length === 1) {
            announcementBar.style.setProperty("--color-bg-hex", bgColor);
            announcementBar.style.setProperty("--color-text-hex", textColor);
            announcement.style.opacity = "1";
            announcement.style.pointerEvents = "auto";
            return;
          }

          if (activeIndex === index) {
            announcementBar.style.setProperty("--color-bg-hex", bgColor);
            announcementBar.style.setProperty("--color-text-hex", textColor);
            announcement.style.opacity = "1";
            announcement.style.pointerEvents = "auto";
            setTimeout(() => {
              activeIndex = activeIndex + 1 === arr.length ? 0 : activeIndex + 1;
              displayAnnouncements();
            }, duration * 1000);
          }

          if (activeIndex !== index) {
            announcement.style.opacity = "0";
            announcement.style.pointerEvents = "none";
            announcement.tabIndex = -1;
          }
        }
      );
    }
  });
}

function initFreeShippingBar() {
  document
    .querySelectorAll<HTMLElement>("[data-free-shipping-bar-target]:not([data-initiated])")
    .forEach((shippingBar) => {
      shippingBar.setAttribute("data-initiated", "");

      const target = +shippingBar.dataset.freeShippingBarTarget;
      const type = shippingBar.dataset.freeShippingBarType as "item_count" | "total_price";
      const content = shippingBar.querySelector<HTMLElement>("[data-free-shipping-bar-content]");
      const message = content.dataset.freeShippingBarMessage;
      const messageSuccess = content.dataset.freeShippingBarMessageSuccess;
      const percentageSlider = shippingBar.querySelector<HTMLElement>(
        "[data-free-shipping-bar-percentage]"
      );

      document.addEventListener("cart:update", (event: CustomEvent) => {
        const current = event.detail[type];
        const difference = Math.max(target - current, 0);
        const percentage = Math.min((current / target) * 100, 100);
        percentageSlider.style.width = `${percentage}%`;

        /* TODO: Content in JS is not a great idea!! */
        if (percentage === 100) {
          content.innerHTML = messageSuccess.replace(/\[remaining]/gi, `${difference}`);
        }
        if (percentage < 100 && type === "item_count") {
          content.innerHTML = message.replace(/\[remaining]/gi, `${difference}`);
        }
        if (percentage < 100 && type === "total_price") {
          content.innerHTML = message.replace(/\[remaining]/gi, window.formatMoney(difference));
        }
      });
    });
}

function initPromotionalItems() {
  const promotionalItems = document.querySelector<HTMLElement>(
    "[data-cart-drawer-promotion-items]"
  );
  if (!promotionalItems) return;
  const settings = JSONParse(promotionalItems.querySelector("[data-section-settings]")?.innerHTML);
  if (settings) {
    render(
      <PromotionalItems settings={settings as CartDrawerSection["settings"]} />,
      promotionalItems
    );
  }
}
